import { useState } from "react";
import type { PricePoint } from "~/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import { PricePointEditor } from "./PricePointEditor";
import { type PricingTypeDefinition, getPricingTypeLabel } from "./pricing-types";

interface AddPricePointProps {
  pricingTypes: PricingTypeDefinition[];
  existingPrices: PricePoint[];
  currency: string;
  onAdd: (pricePoint: PricePoint) => void;
}

export function AddPricePoint({ pricingTypes, existingPrices, currency, onAdd }: AddPricePointProps) {
  const [selectedType, setSelectedType] = useState<string | null>(null);

  if (!selectedType) {
    return (
      <Select value="" onValueChange={setSelectedType}>
        <SelectTrigger size="sm" className="h-8 w-auto min-w-40 text-sm" aria-label="Add pricing">
          <SelectValue placeholder="Add pricing..." />
        </SelectTrigger>
        <SelectContent>
          {pricingTypes.map((pt) => (
            <SelectItem key={pt.type} value={pt.type}>
              {pt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  const handleSave = (point: PricePoint) => {
    if (isDuplicate(existingPrices, point)) {
      const typeName = getPricingTypeLabel(point.type).toLowerCase();
      const desc = point.label ? ` "${point.label}"` : "";
      throw new Error(`A ${typeName}${desc} price already exists. Add a description to distinguish it.`);
    }
    onAdd(point);
    setSelectedType(null);
  };

  return (
    <PricePointEditor
      value={{ type: selectedType, amount: 0 }}
      currency={currency}
      onChange={handleSave}
      initialEdit
      onCancel={() => setSelectedType(null)}
    />
  );
}

function isDuplicate(existing: PricePoint[], point: PricePoint): boolean {
  return existing.some((p) => p.type === point.type && (p.label ?? undefined) === (point.label ?? undefined));
}
